import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import TaskComposer from '../components/TaskComposer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Check, X, Mail, Calendar, CalendarDays, BarChart3, MousePointerSquareDashed, ArrowRight, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RecruiterDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    openPositions: 0,
    applications: 0,
    screening: 0,
    interviewRecruiter: 0,
    interviewHiringManager: 0,
    onsiteInterview: 0,
    decision: 0,
    contract: 0,
    rejected: 0,
    withdrawn: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [taskCandidates, setTaskCandidates] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [dismissedNotifIds, setDismissedNotifIds] = useState(() => {
    try {
      const raw = localStorage.getItem('jobasti.dismissedNotifIds');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const persistDismissed = (next) => {
    try {
      localStorage.setItem('jobasti.dismissedNotifIds', JSON.stringify(Array.from(next)));
    } catch (_e) { /* ignore quota */ }
  };

  const dismissNotif = (id) => {
    const next = new Set(dismissedNotifIds);
    next.add(id);
    setDismissedNotifIds(next);
    persistDismissed(next);
  };

  const dismissAllVisible = () => {
    const next = new Set(dismissedNotifIds);
    notifications.forEach((n) => next.add(n.id));
    setDismissedNotifIds(next);
    persistDismissed(next);
  };
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [scatterData, setScatterData] = useState([]);
  const [dayRange, setDayRange] = useState(60);
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [overviewMode, setOverviewMode] = useState('scatter'); // scatter | roleHealth
  const [roleHealthData, setRoleHealthData] = useState([]);

  // Lasso selection on scatter chart
  const [lassoSelectedIds, setLassoSelectedIds] = useState([]);
  const [lassoRect, setLassoRect] = useState(null); // { x, y, w, h } in chart-local coords
  const chartWrapperRef = useRef(null);
  const lassoStartRef = useRef(null); // { x, y, mouseX, mouseY }
  const lassoActiveRef = useRef(false); // true briefly after lasso to suppress dot click navigation
  const dragOccurredRef = useRef(false);

  const currentDate = new Date().toLocaleDateString('de-DE');
  const timeSlots = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"];
  
  // Get current time for the red line indicator
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  const TODO_KEY = `jobasti_todos_${userProfile?.id}`;
  const [todoItems, setTodoItems] = useState([]);
  const [editingTodoId, setEditingTodoId] = useState(null);

  const MAX_TODO_WORDS = 30;

  // Load todos from DB when userProfile is available (synced across browsers/devices)
  useEffect(() => {
    if (userProfile?.id) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/todos`, { params: { userId: userProfile.id } })
        .then((r) => setTodoItems(Array.isArray(r.data?.todos) ? r.data.todos : []))
        .catch(() => setTodoItems([]));
    }
  }, [userProfile?.id]);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', time: '10:00', duration: '30', candidate: '' });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile?.id) return;
    fetchCalendarEvents(selectedDate);
  }, [userProfile, selectedDate]);

  // Auto-scroll calendar to current time (center)
  useEffect(() => {
    const container = document.getElementById('calendar-scroll');
    const line = document.getElementById('calendar-now-line');
    if (container && line) {
      const lineTop = line.offsetTop;
      container.scrollTop = lineTop - container.clientHeight / 2;
    }
  }, [calendarEvents]);

  const fetchCalendarEvents = async (dateObj) => {
    try {
      if (!userProfile?.id) return;
      const date = dateObj || new Date();
      const ymd = date.toISOString().slice(0, 10);

      // First try to fetch scheduled interviews from database for this date
      const interviewsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/interviews/upcoming/${userProfile.id}`,
        { params: { date: ymd } }
      );

      if (interviewsResponse.data?.success && interviewsResponse.data?.data?.length > 0) {
        setCalendarEvents(interviewsResponse.data.data);
        setCalendarConnected(true);
        return;
      }

      // If no interviews in DB, check Google Calendar
      const statusResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/status`,
        { params: { userId: userProfile.id } }
      );

      if (!statusResponse.data?.connected) {
        setCalendarConnected(false);
        setCalendarEvents([]);
        return;
      }

      setCalendarConnected(true);

      // Fetch events from Google Calendar for the selected day
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      const eventsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`,
        {
          params: {
            userId: userProfile.id,
            timeMin: dayStart.toISOString(),
            timeMax: dayEnd.toISOString()
          }
        }
      );

      if (eventsResponse.data?.success && eventsResponse.data?.events) {
        const formattedEvents = eventsResponse.data.events.map(event => {
          const startTime = new Date(event.start);
          return {
            time: startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            title: event.title,
            id: event.id,
            meetLink: event.meetLink || null,
          };
        });
        setCalendarEvents(formattedEvents);
      } else {
        setCalendarEvents([]);
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setCalendarConnected(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      if (userProfile?.organizationId) {
        // Fetch positions for this organization only (includes drafts/closed)
        const posResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile.organizationId}`
        );
        
        // Fetch applications/candidates for this organization's positions
        const candResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile.organizationId}/candidates`
        );

        const candidates = candResponse.data?.data || [];

        // Applicants available for @-tagging in the To-Do composer
        setTaskCandidates(candidates.map((c) => ({
          applicationId: c.applicationId || c.id,
          name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Candidate',
          role: c.positionTitle || '',
        })).filter((c) => c.applicationId));

        // Count by status - map database status to pipeline stages
        const statusCounts = {
          screening: 0,         // reviewing
          interviewRecruiter: 0, // shortlisted
          interviewHiring: 0,   // interviewed
          onsiteInterview: 0,   // onsite_interview
          decision: 0,          // decision
          contract: 0,          // offered, accepted
          rejected: 0,          // rejected
          withdrawn: 0          // withdrawn
        };

        candidates.forEach(c => {
          const status = (c.applicationStatus || c.status || 'pending').toLowerCase();
          switch (status) {
            case 'reviewing':
              statusCounts.screening++;
              break;
            case 'shortlisted':
              statusCounts.interviewRecruiter++;
              break;
            case 'interviewed':
              statusCounts.interviewHiring++;
              break;
            case 'onsite_interview':
              statusCounts.onsiteInterview++;
              break;
            case 'decision':
              statusCounts.decision++;
              break;
            case 'offered':
            case 'accepted':
              statusCounts.contract++;
              break;
            case 'rejected':
              statusCounts.rejected++;
              break;
            case 'withdrawn':
              statusCounts.withdrawn++;
              break;
            // pending and any other status don't need separate counting
            // as Applications shows ALL candidates
          }
        });

        const openPositionsCount = (posResponse.data?.data || []).filter(
          p => (p.status || '').toLowerCase() === 'published'
        ).length;

        setStats({
          openPositions: openPositionsCount,
          applications: candidates.length,  // Total count of ALL applications
          screening: statusCounts.screening,
          interviewRecruiter: statusCounts.interviewRecruiter,
          interviewHiringManager: statusCounts.interviewHiring,
          onsiteInterview: statusCounts.onsiteInterview,
          decision: statusCounts.decision,
          contract: statusCounts.contract,
          rejected: statusCounts.rejected,
          withdrawn: statusCounts.withdrawn
        });

        // Create notifications from recent applications — applicant name + match score
        const recentApps = candidates.slice(0, 50).map(c => {
          const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Anonymous';
          const scoreLabel = (c.matchScore != null) ? `${Math.round(c.matchScore)}% match` : 'Not scored yet';
          return {
            id: c.applicationId || c.id || `${c.firstName}-${c.appliedAt}`,
            applicationId: c.applicationId || null,
            position: c.positionTitle || c.appliedPosition || 'Unknown Position',
            applicantName: name,
            matchScore: (c.matchScore != null) ? Math.round(c.matchScore) : null,
            date: new Date(c.appliedAt || c.createdAt).toLocaleDateString('de-DE'),
            time: new Date(c.appliedAt || c.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
            message: `New application from ${name} · ${scoreLabel}`,
          };
        });
        
        setNotifications(recentApps);

        // Build scatter plot data from candidates with matchScore
        const now = new Date();
        const scatter = candidates
          .filter(c => c.matchScore != null && c.appliedAt)
          .map(c => {
            const appliedDate = new Date(c.appliedAt);
            const daysSince = Math.max(0, Math.round((now - appliedDate) / (1000 * 60 * 60 * 24)));
            return {
              x: daysSince,
              y: c.matchScore,
              name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
              position: c.positionTitle || c.appliedPosition || '',
              applicationId: c.applicationId,
              appliedPositionId: c.appliedPositionId,
              status: c.status,
            };
          });
        setScatterData(scatter);

        // Fetch role health data
        try {
          const healthRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/positions/organization/${userProfile.organizationId}/role-health`
          );
          if (healthRes.data.success) setRoleHealthData(healthRes.data.data || []);
        } catch (e) { /* role health optional */ }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTodos = (items) => {
    setTodoItems(items);
    if (!userProfile?.id) return;
    axios
      .put(`${process.env.REACT_APP_BACKEND_URL}/api/todos`, { todos: items }, { params: { userId: userProfile.id } })
      .catch((err) => console.warn('Failed to save todos:', err));
  };

  const toggleTodo = (id) => {
    const updated = todoItems.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    saveTodos(updated);
  };

  const removeTodo = (id) => {
    saveTodos(todoItems.filter(item => item.id !== id));
  };

  // Lasso handlers
  const handleLassoMouseDown = (e) => {
    if (!chartWrapperRef.current) return;
    if (e.button !== 0) return;
    const rect = chartWrapperRef.current.getBoundingClientRect();
    lassoStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    dragOccurredRef.current = false;
    // Do NOT setLassoRect here — would re-render and break dot click events.
  };

  const handleLassoMouseMove = (e) => {
    if (!lassoStartRef.current || !chartWrapperRef.current) return;
    const rect = chartWrapperRef.current.getBoundingClientRect();
    const curX = e.clientX - rect.left;
    const curY = e.clientY - rect.top;
    const startX = lassoStartRef.current.x;
    const startY = lassoStartRef.current.y;
    const dx = curX - startX;
    const dy = curY - startY;
    if (!dragOccurredRef.current && Math.abs(dx) + Math.abs(dy) > 4) {
      dragOccurredRef.current = true;
    }
    if (!dragOccurredRef.current) return; // pure click in progress — don't render rect
    setLassoRect({
      x: Math.min(startX, curX),
      y: Math.min(startY, curY),
      w: Math.abs(dx),
      h: Math.abs(dy),
    });
  };

  const finalizeLasso = () => {
    if (!lassoStartRef.current || !chartWrapperRef.current) {
      lassoStartRef.current = null;
      setLassoRect(null);
      return;
    }
    if (!dragOccurredRef.current || !lassoRect) {
      // Pure click — let the dot's onClick handle navigation
      lassoStartRef.current = null;
      setLassoRect(null);
      return;
    }
    const wrapperRect = chartWrapperRef.current.getBoundingClientRect();
    const dots = chartWrapperRef.current.querySelectorAll('[data-application-id]');
    const ids = [];
    dots.forEach((dot) => {
      const r = dot.getBoundingClientRect();
      const cx = r.left + r.width / 2 - wrapperRect.left;
      const cy = r.top + r.height / 2 - wrapperRect.top;
      if (
        cx >= lassoRect.x && cx <= lassoRect.x + lassoRect.w &&
        cy >= lassoRect.y && cy <= lassoRect.y + lassoRect.h
      ) {
        ids.push(dot.getAttribute('data-application-id'));
      }
    });
    if (ids.length > 0) {
      setLassoSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
      lassoActiveRef.current = true;
      setTimeout(() => { lassoActiveRef.current = false; }, 150);
    }
    lassoStartRef.current = null;
    setLassoRect(null);
  };

  const handleLassoMouseUp = () => finalizeLasso();
  const handleLassoMouseLeave = () => {
    if (lassoStartRef.current) finalizeLasso();
  };

  const removeFromLasso = (id) => {
    setLassoSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const clearLasso = () => setLassoSelectedIds([]);

  const moveLassoToCandidates = () => {
    navigate('/candidates', { state: { stage: 'all', preselectedIds: lassoSelectedIds } });
  };

  // Scatter plot helpers
  const getDotColor = (score) => {
    if (score >= 70) return { fill: '#10b981', light: '#6ee7b7', dark: '#047857' };
    if (score >= 40) return { fill: '#f59e0b', light: '#fcd34d', dark: '#b45309' };
    return { fill: '#ef4444', light: '#fca5a5', dark: '#b91c1c' };
  };

  const getGradeLabel = (score) => {
    if (score >= 70) return 'A';
    if (score >= 40) return 'B';
    return 'C';
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy) return null;
    const colors = getDotColor(payload.y);
    const id = `grad-${payload.applicationId}`;
    const isSelected = lassoSelectedIds.includes(payload.applicationId);
    return (
      <g>
        <defs>
          <radialGradient id={id} cx="35%" cy="35%" r="65%">
            <stop offset="0%" stopColor={colors.light} />
            <stop offset="60%" stopColor={colors.fill} />
            <stop offset="100%" stopColor={colors.dark} />
          </radialGradient>
        </defs>
        {isSelected && (
          <circle cx={cx} cy={cy} r={9} fill="none" stroke="#2563eb" strokeWidth={2} strokeDasharray="2 2" data-testid={`scatter-ring-${payload.applicationId}`} />
        )}
        <circle
          cx={cx} cy={cy} r={isSelected ? 6 : 5}
          fill={`url(#${id})`}
          stroke={isSelected ? '#1d4ed8' : colors.dark}
          strokeWidth={isSelected ? 1.5 : 1}
          style={{ cursor: 'pointer', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}
          data-testid={`scatter-dot-${payload.applicationId}`}
          data-application-id={payload.applicationId}
          onClick={(e) => {
            e.stopPropagation();
            if (lassoActiveRef.current) return; // suppress click right after a lasso drag
            if (payload.applicationId) navigate(`/candidates/${payload.applicationId}`);
          }}
        />
      </g>
    );
  };

  const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const colors = getDotColor(d.y);
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 text-sm" data-testid="scatter-tooltip">
        <p className="font-semibold text-gray-800">{d.name}</p>
        <p className="text-gray-500 text-xs mb-1.5">{d.position}</p>
        <div className="flex gap-3 items-center">
          <span className="text-xs font-medium" style={{ color: colors.dark }}>{d.y}% ({getGradeLabel(d.y)})</span>
          <span className="text-xs text-gray-400">{d.x} days ago</span>
        </div>
      </div>
    );
  };

  const activities = [
    { label: "Open positions", count: stats.openPositions, color: "bg-blue-500", path: "/positions" },
    { label: "Applications", count: stats.applications, color: "bg-pink-400", path: "/candidates", stage: "all" },
    { label: "Screening", count: stats.screening, color: "bg-purple-400", path: "/candidates", stage: "screening" },
    { label: "Interview Recruiter", count: stats.interviewRecruiter, color: "bg-sky-400", path: "/candidates", stage: "interview_recruiter" },
    { label: "Interview Hiring Manager", count: stats.interviewHiringManager, color: "bg-blue-400", path: "/candidates", stage: "interview_hiring" },
    { label: "Onsite Interview", count: stats.onsiteInterview, color: "bg-blue-600", path: "/candidates", stage: "onsite_interview" },
    { label: "Decision", count: stats.decision, color: "bg-amber-400", path: "/candidates", stage: "decision" },
    { label: "Contract", count: stats.contract, color: "bg-green-500", path: "/candidates", stage: "contract" },
    { label: "Rejected", count: stats.rejected, color: "bg-orange-400", path: "/candidates", stage: "rejected" },
    { label: "Withdrawn", count: stats.withdrawn, color: "bg-red-500", path: "/candidates", stage: "withdrawn" }
  ];

  // Calculate red line position based on current time (for hours 9-18)
  const getRedLineIndex = () => {
    if (currentHour < 9 || currentHour > 18) return -1;
    return currentHour - 9;
  };

  const redLineIndex = getRedLineIndex();

  return (
    <RecruiterLayout title={`Welcome${userProfile?.firstName ? ', ' + userProfile.firstName : ''}`} subtitle={currentDate}>
      
      <div className="max-w-7xl mx-auto p-4 lg:p-6" data-testid="recruiter-dashboard">
        {/* Top Row - Calendar, Activities, Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 mb-6">
          
          {/* Calendar - Left Column */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold">Calendar</CardTitle>
                  {calendarConnected ? (
                    <Button
                      variant="link"
                      className="text-blue-500 text-sm p-0 h-auto font-normal"
                      onClick={() => {
                        const d = new Date(selectedDate);
                        setNewEvent({ title: '', time: '10:00', duration: '30', candidate: '' });
                        setShowAddEventModal(true);
                      }}
                      data-testid="add-event-btn"
                    >
                      + add event
                    </Button>
                  ) : (
                    <Button
                      variant="link"
                      className="text-blue-500 text-sm p-0 h-auto font-normal"
                      onClick={() => navigate('/settings')}
                    >
                      Connect Calendar
                    </Button>
                  )}
                </div>
                {/* Date nav row */}
                <div className="flex items-center justify-between mt-1">
                  <button
                    onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d); }}
                    className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                    data-testid="cal-prev-day"
                    aria-label="Previous day"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSelectedDate(new Date())}
                    className="text-sm text-gray-400 hover:text-blue-600 transition-colors"
                    data-testid="cal-current-date"
                  >
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </button>
                  <button
                    onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d); }}
                    className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                    data-testid="cal-next-day"
                    aria-label="Next day"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                {calendarConnected && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    Google Calendar synced
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {!calendarConnected ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-3">Connect your Google Calendar to see your events here</p>
                    <Button 
                      size="sm" 
                      onClick={() => navigate('/settings')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Connect Calendar
                    </Button>
                  </div>
                ) : (
                  <div className="relative max-h-[320px] overflow-y-auto" id="calendar-scroll" data-testid="calendar-scroll">
                    {timeSlots.map((time, index) => {
                      const slotHour = time.split(':')[0];
                      const event = calendarEvents.find(e => e.time && e.time.split(':')[0] === slotHour);
                      const isRedLine = index === redLineIndex;
                      const isSelected = selectedEvent?.time === event?.time;
                      return (
                        <div key={time} className="relative">
                          <div className="flex items-center min-h-[36px]">
                            <span className="w-12 text-sm text-gray-400 flex-shrink-0">{time}</span>
                            <div className="flex-1 ml-3 border-b border-gray-300 h-full min-h-[36px] flex items-center relative">
                              {event && (
                                <div className="relative">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedEvent(isSelected ? null : event); }}
                                    className={`text-sm px-2 py-0.5 rounded cursor-pointer transition-colors truncate max-w-full ${
                                      event.interviewType === 'async'
                                        ? (isSelected ? 'text-white bg-purple-600' : 'text-purple-700 bg-purple-100 hover:bg-purple-200')
                                        : (isSelected ? 'text-white bg-blue-600' : 'text-blue-700 bg-blue-100 hover:bg-blue-200')
                                    }`}
                                    data-testid={`event-${event.time}`}
                                  >
                                    {event.interviewType === 'async' && <span className="mr-1">▶</span>}
                                    <span className="font-medium">{event.time}</span> {event.title}
                                  </button>
                                  {isSelected && (
                                    <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-sm w-[220px]" data-testid="event-detail">
                                      <div className="flex justify-between items-start mb-2">
                                        <div className="min-w-0">
                                          <p className="font-semibold text-gray-800 text-xs leading-tight">{selectedEvent.title}</p>
                                          {selectedEvent.interviewType === 'async' && (
                                            <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md border ${
                                              selectedEvent.asyncStatus === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                              selectedEvent.asyncStatus === 'in_progress' ? 'bg-red-50 text-red-700 border-red-200' :
                                              selectedEvent.asyncStatus === 'expired' ? 'bg-gray-50 text-gray-500 border-gray-200' :
                                              'bg-purple-50 text-purple-700 border-purple-200'
                                            }`}>
                                              ▶ Video · {selectedEvent.asyncStatus === 'completed' ? 'Completed' : selectedEvent.asyncStatus === 'in_progress' ? 'Live now' : selectedEvent.asyncStatus === 'expired' ? 'Expired' : 'Scheduled'}
                                            </span>
                                          )}
                                        </div>
                                        <button onClick={(e) => { e.stopPropagation(); setSelectedEvent(null); }} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                      <div className="space-y-1 text-xs text-gray-600">
                                        <p><span className="text-gray-400">Time:</span> {selectedEvent.time}</p>
                                        {selectedEvent.prospectName && <p><span className="text-gray-400">Candidate:</span> {selectedEvent.prospectName}</p>}
                                        {selectedEvent.position && <p><span className="text-gray-400">Position:</span> {selectedEvent.position}</p>}
                                      </div>
                                      {selectedEvent.meetLink && (
                                        <a
                                          href={selectedEvent.meetLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-white rounded text-xs font-medium ${selectedEvent.interviewType === 'async' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                          data-testid="event-join-link"
                                        >
                                          {selectedEvent.interviewType === 'async' ? 'Open interview' : 'Join Meeting'}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Red line indicator for current time (only when viewing today) */}
                          {isRedLine && selectedDate.toDateString() === new Date().toDateString() && (
                            <div id="calendar-now-line" className="absolute left-12 right-0 flex items-center z-10" style={{ top: `${(currentMinutes / 60) * 36}px` }}>
                              <div className="w-2.5 h-2.5 bg-red-500 rounded-full -ml-1"></div>
                              <div className="flex-1 h-0.5 bg-red-500"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {calendarEvents.length === 0 && (
                      <p className="text-center text-sm text-gray-400 py-4">No events scheduled for today</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Activities - Middle Column */}
          <div className="lg:col-span-4">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">Activities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {activities.map((activity, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-md cursor-pointer hover:bg-blue-50 transition-colors"
                      onClick={() => navigate(activity.path, activity.stage ? { state: { stage: activity.stage } } : undefined)}
                      data-testid={`activity-${activity.stage || 'positions'}`}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${activity.color} flex-shrink-0`}></div>
                      <span className="text-sm text-gray-600 w-6 text-center font-medium">{activity.count}</span>
                      <span className="text-sm text-gray-700">{activity.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notifications - Right Column */}
          <div className="lg:col-span-4">
            <Card className="h-full border-slate-200 shadow-sm rounded-xl">
              <CardHeader className="pb-3 border-b border-slate-100">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Bell className="w-4 h-4 text-blue-600" />
                    </span>
                    Notifications
                    {notifications.filter(n => !dismissedNotifIds.has(n.id)).length > 0 && (
                      <span className="text-[11px] font-semibold bg-blue-600 text-white rounded-full px-2 py-0.5 min-w-[20px] text-center">
                        {notifications.filter(n => !dismissedNotifIds.has(n.id)).length}
                      </span>
                    )}
                  </CardTitle>
                  <Button
                    variant="link"
                    className="text-blue-600 text-sm p-0 h-auto font-medium hover:text-blue-700"
                    onClick={() => setShowNotifModal(true)}
                    data-testid="notifications-see-all-btn"
                  >
                    See all
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {notifications.filter(n => !dismissedNotifIds.has(n.id)).slice(0, 4).length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-400 flex flex-col items-center gap-2" data-testid="notifications-empty">
                      <span className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-300" />
                      </span>
                      You&apos;re all caught up.
                    </div>
                  ) : (
                    notifications.filter(n => !dismissedNotifIds.has(n.id)).slice(0, 4).map((notification) => (
                      <div
                        key={notification.id}
                        role={notification.applicationId ? 'button' : undefined}
                        tabIndex={notification.applicationId ? 0 : undefined}
                        onClick={() => {
                          if (notification.applicationId) navigate(`/candidates/${notification.applicationId}`);
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && notification.applicationId) {
                            e.preventDefault();
                            navigate(`/candidates/${notification.applicationId}`);
                          }
                        }}
                        className={`group relative border border-slate-200 rounded-2xl p-4 bg-white hover:border-blue-300 hover:shadow-[0_6px_20px_rgba(37,99,235,0.10)] transition-all ${
                          notification.applicationId ? 'cursor-pointer' : ''
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissNotif(notification.id);
                          }}
                          className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          aria-label="Dismiss notification"
                          title="Dismiss"
                          data-testid={`notification-dismiss-${notification.id}`}
                        >
                          <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </button>
                        <div className="flex gap-3.5">
                          {/* Blue application emblem */}
                          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Mail className="w-[18px] h-[18px] text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-900 truncate">{notification.applicantName}</p>
                              <span className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0">{notification.date}</span>
                            </div>
                            <p className="text-xs font-medium text-blue-600 truncate mt-1">{notification.position}</p>
                            <p className="text-xs text-slate-500 leading-relaxed mt-2 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="mt-2.5">
                              {notification.matchScore != null ? (
                                <span
                                  className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                    notification.matchScore >= 80
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : notification.matchScore >= 60
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-rose-100 text-rose-700'
                                  }`}
                                  data-testid={`notification-match-${notification.id}`}
                                >
                                  {notification.matchScore}% match
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-400 whitespace-nowrap" data-testid={`notification-match-${notification.id}`}>Not scored yet</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Row - To Do and Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          
          {/* To Do - Left Column */}
          <div className="lg:col-span-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold">To Do</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {/* Shared task composer — same concept as the Tasks page (@mention, details, dates) */}
                <div className="mb-2">
                  <TaskComposer candidates={taskCandidates} onCreate={(item) => saveTodos([...todoItems, item])} />
                </div>

                <div className="space-y-1">
                  {todoItems.length === 0 && (
                    <p className="text-sm text-gray-400 py-4 text-center">No tasks yet. Click "Add a task" to get started.</p>
                  )}
                  {todoItems.map((item) => (
                    editingTodoId === item.id ? (
                      <div key={item.id} className="py-2 border-b border-gray-100 last:border-0" data-testid={`task-edit-${item.id}`}>
                        <TaskComposer
                          candidates={taskCandidates}
                          initial={item}
                          onSave={(u) => { saveTodos(todoItems.map((x) => (x.id === u.id ? u : x))); setEditingTodoId(null); }}
                          onCancelEdit={() => setEditingTodoId(null)}
                        />
                      </div>
                    ) : (
                    <div key={item.id} className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0 group">
                      <button 
                        onClick={() => toggleTodo(item.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          item.completed 
                            ? 'bg-blue-500 border-blue-500 text-white' 
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {item.completed && <Check className="w-3 h-3" />}
                      </button>
                      <button type="button" onClick={() => setEditingTodoId(item.id)} className="flex-1 min-w-0 text-left" data-testid={`task-open-${item.id}`}>
                        <span className={`text-sm leading-relaxed block ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                          {item.text}
                        </span>
                        {item.details && <span className={`text-xs block mt-0.5 ${item.completed ? 'text-gray-300' : 'text-gray-500'}`}>{item.details}</span>}
                        {(item.dueDate || (item.mentions || []).length > 0) && (
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {item.dueDate && (() => {
                              const d = new Date(item.dueDate + 'T00:00:00');
                              const t0 = new Date(); t0.setHours(0,0,0,0);
                              const tm = new Date(t0); tm.setDate(tm.getDate() + 1);
                              const label = d.getTime() === t0.getTime() ? 'Today' : d.getTime() === tm.getTime() ? 'Tomorrow' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                              const overdue = d < t0 && !item.completed;
                              return <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${overdue ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>{label}</span>;
                            })()}
                            {(item.mentions || []).map((m, i) => (
                              <span key={i} className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full">{m.name}</span>
                            ))}
                          </div>
                        )}
                      </button>
                      <button 
                        onClick={() => removeTodo(item.id)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Activity Chart - Right Column (spans 8 cols) */}
          <div className="lg:col-span-8">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-semibold" data-testid="overview-card-title">
                    {overviewMode === 'scatter' ? 'Candidate Overview' : 'Positions Overview'}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* View toggle — temporarily hidden while Positions Overview is being reworked
                    <button
                      onClick={() => {
                        const next = overviewMode === 'scatter' ? 'roleHealth' : 'scatter';
                        // Clamp day range when switching back to scatter — scatter only handles 30/60/90
                        if (next === 'scatter' && dayRange > 90) setDayRange(90);
                        setOverviewMode(next);
                      }}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${overviewMode === 'roleHealth' ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-blue-600 hover:bg-gray-50'}`}
                      data-testid="toggle-role-health"
                      title={overviewMode === 'scatter' ? 'Positions Overview' : 'Candidate Overview'}
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                    </button>
                    */}
                    {/* Day range — visible in both modes */}
                    <div className="relative">
                      <button
                        onClick={() => setShowDayPicker(!showDayPicker)}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors px-2 py-1 rounded-md hover:bg-gray-50"
                        data-testid="day-range-picker"
                      >
                        <CalendarDays className="w-3.5 h-3.5" />
                        <span>{dayRange}d</span>
                      </button>
                      {showDayPicker && (
                        <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[80px]" data-testid="day-range-dropdown">
                          {(overviewMode === 'roleHealth' ? [30, 60, 90, 180, 360] : [30, 60, 90]).map(d => (
                            <button
                              key={d}
                              onClick={() => { setDayRange(d); setShowDayPicker(false); }}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition-colors ${dayRange === d ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-600'}`}
                              data-testid={`day-range-${d}`}
                            >
                              {d} days
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 relative" data-testid="scatter-chart-container" key={overviewMode}>
                {overviewMode === 'scatter' ? (
                  <>
                    {scatterData.length === 0 ? (
                  <div className="flex items-center justify-center h-52 text-sm text-gray-400">
                    No scored candidates yet
                  </div>
                ) : (
                  <div
                    ref={chartWrapperRef}
                    className="h-60 scatter-chart-wrapper relative select-none"
                    onMouseDown={handleLassoMouseDown}
                    onMouseMove={handleLassoMouseMove}
                    onMouseUp={handleLassoMouseUp}
                    onMouseLeave={handleLassoMouseLeave}
                    data-testid="scatter-lasso-wrapper"
                  >
                    <style>{`
                      .scatter-chart-wrapper svg,
                      .scatter-chart-wrapper svg *,
                      .scatter-chart-wrapper .recharts-surface,
                      .scatter-chart-wrapper .recharts-wrapper {
                        outline: none !important;
                        border: none !important;
                      }
                      .scatter-chart-wrapper .recharts-surface:focus,
                      .scatter-chart-wrapper .recharts-surface:focus-visible,
                      .scatter-chart-wrapper svg:focus,
                      .scatter-chart-wrapper svg:focus-visible {
                        outline: none !important;
                        box-shadow: none !important;
                      }
                    `}</style>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }} accessibilityLayer={false}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          type="number"
                          dataKey="x"
                          name="Days"
                          domain={[0, dayRange]}
                          ticks={dayRange === 30 ? [0, 5, 10, 15, 20, 25, 30] : dayRange === 90 ? [0, 15, 30, 45, 60, 75, 90] : [0, 10, 20, 30, 40, 50, 60]}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          name="Score"
                          domain={[0, 100]}
                          ticks={[0, 50, 100]}
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => {
                            if (v === 100) return 'A';
                            if (v === 50) return 'B';
                            return 'C';
                          }}
                        />
                        <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: '4 4', stroke: '#d1d5db', strokeWidth: 1 }} trigger="hover" />
                        <Scatter
                          data={scatterData.filter(d => d.x <= dayRange)}
                          shape={<CustomDot />}
                          activeShape={false}
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                    {/* Lasso rectangle overlay */}
                    {lassoRect && (lassoRect.w > 2 || lassoRect.h > 2) && (
                      <div
                        className="absolute pointer-events-none border border-blue-500 bg-blue-500/10 rounded-sm"
                        style={{ left: lassoRect.x, top: lassoRect.y, width: lassoRect.w, height: lassoRect.h }}
                        data-testid="lasso-rect"
                      />
                    )}
                    {/* Lasso hint (only when nothing selected and no drag in progress) */}
                    {lassoSelectedIds.length === 0 && !lassoRect && (
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50/90 border border-gray-200 text-[10px] text-gray-500 pointer-events-none" data-testid="lasso-hint">
                        <MousePointerSquareDashed className="w-3 h-3" />
                        Drag to select
                      </div>
                    )}
                  </div>
                )}
                {/* Lasso Selection Mini-Panel — floats over the chart card */}
                {lassoSelectedIds.length > 0 && (
                  <div
                    className="absolute right-4 top-16 w-72 border border-blue-200 bg-white rounded-xl shadow-2xl overflow-hidden z-30 animate-in fade-in slide-in-from-top-2 duration-200"
                    data-testid="lasso-panel"
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-blue-100 bg-blue-50">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-blue-800">{lassoSelectedIds.length} selected</span>
                        <button onClick={clearLasso} className="text-xs text-blue-600 hover:underline" data-testid="lasso-clear">Clear all</button>
                      </div>
                      <button
                        onClick={clearLasso}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        data-testid="lasso-close"
                        aria-label="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-blue-50">
                      {lassoSelectedIds.map((id) => {
                        const d = scatterData.find((s) => s.applicationId === id);
                        if (!d) return null;
                        const colors = getDotColor(d.y);
                        return (
                          <div key={id} className="flex items-center justify-between px-3 py-2 hover:bg-blue-50/60" data-testid={`lasso-item-${id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.fill }} />
                              <div className="min-w-0">
                                <p className="text-sm text-gray-800 truncate">{d.name}</p>
                                <p className="text-[11px] text-gray-500 truncate">{d.position}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: colors.light, color: colors.dark }}>
                                {d.y}%
                              </span>
                              <button
                                onClick={() => removeFromLasso(id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                data-testid={`lasso-remove-${id}`}
                                aria-label="Remove"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-2 border-t border-blue-100 bg-blue-50/40">
                      <Button
                        size="sm"
                        onClick={moveLassoToCandidates}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
                        data-testid="lasso-move-btn"
                      >
                        Move to Candidates
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
                  </>
                ) : (
                  /* Positions Overview — Half-donut lifecycle chart */
                  <div className="py-2" data-testid="role-health-view">
                    {(() => {
                      const inRange = roleHealthData.filter(r => (r.daysElapsed ?? 0) <= dayRange);
                      if (inRange.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-52 text-sm text-gray-400">
                            {roleHealthData.length === 0 ? 'No published positions yet' : `No positions opened in the last ${dayRange} days`}
                          </div>
                        );
                      }
                      // Sort by urgency descending (urgent on the left, healthy on the right)
                      const sorted = [...inRange].sort((a, b) => b.elapsed - a.elapsed);
                      const total = sorted.length;
                      const totalApplicants = sorted.reduce((s, r) => s + r.applicantCount, 0);

                      // Lifecycle color: urgent=red, mid=amber, healthy/closed=green
                      const getColor = (role) => {
                        if (role.health === 'closed' && role.advancedCount > 0) return '#22c55e';
                        if (role.elapsed >= 0.7) return '#ef4444';
                        if (role.elapsed >= 0.4) return '#f59e0b';
                        return '#22c55e';
                      };

                      // Half-donut geometry: equal-width segments along the top half
                      const cx = 220, cy = 175, R = 150, r = 92;
                      const sliceAngle = Math.PI / total;
                      const gapAngle = total > 1 ? Math.min(sliceAngle * 0.06, 0.025) : 0;
                      const polar = (radius, angle) => [cx + radius * Math.cos(angle), cy - radius * Math.sin(angle)];

                      const slices = sorted.map((role, i) => {
                        const a1 = Math.PI - i * sliceAngle - gapAngle / 2;
                        const a2 = Math.PI - (i + 1) * sliceAngle + gapAngle / 2;
                        const largeArc = a1 - a2 > Math.PI ? 1 : 0;
                        const [x1, y1] = polar(R, a1);
                        const [x2, y2] = polar(R, a2);
                        const [x3, y3] = polar(r, a2);
                        const [x4, y4] = polar(r, a1);
                        const d = `M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${largeArc} 0 ${x4} ${y4} Z`;
                        return { ...role, d, fill: getColor(role) };
                      });

                      const healthy = slices.filter(s => s.fill === '#22c55e').length;
                      const urgent = slices.filter(s => s.fill === '#ef4444').length;
                      const attention = slices.filter(s => s.fill === '#f59e0b').length;
                      const healthPct = total > 0 ? Math.round((healthy / total) * 100) : 0;

                      // Background ring — full half-donut so the shape is obvious even with few segments
                      const [bgX1, bgY1] = polar(R, Math.PI);
                      const [bgX2, bgY2] = polar(R, 0);
                      const [bgX3, bgY3] = polar(r, 0);
                      const [bgX4, bgY4] = polar(r, Math.PI);
                      const bgD = `M ${bgX1} ${bgY1} A ${R} ${R} 0 0 1 ${bgX2} ${bgY2} L ${bgX3} ${bgY3} A ${r} ${r} 0 0 0 ${bgX4} ${bgY4} Z`;

                      return (
                        <div className="flex flex-col items-center">
                          <div className="relative w-full" style={{ maxWidth: 460, height: 210 }}>
                            <svg viewBox="0 0 440 200" className="w-full h-full">
                              {/* Background half-donut */}
                              <path d={bgD} fill="#f1f5f9" />
                              {/* Slices */}
                              {slices.map((s) => (
                                <path
                                  key={s.id}
                                  d={s.d}
                                  fill={s.fill}
                                  stroke="white"
                                  strokeWidth="1.5"
                                  className="cursor-pointer transition-all duration-200 hover:brightness-110"
                                  onClick={() => navigate('/candidates', { state: { stage: 'all' } })}
                                  data-testid={`role-slice-${s.id}`}
                                >
                                  <title>{`${s.title}\n${s.daysElapsed} days open | ${s.applicantCount} applicants\nBest match: ${s.bestMatch}%\n${s.health === 'closed' ? 'Fulfilled' : s.daysRemaining + ' days remaining'}`}</title>
                                </path>
                              ))}
                              {/* Baseline */}
                              <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke="#e2e8f0" strokeWidth="1" />
                              {/* Center stat */}
                              <text x={cx} y={cy - 28} textAnchor="middle" fontSize="42" fontWeight="700" fill="#111827" className="tabular-nums">{healthPct}%</text>
                              <text x={cx} y={cy - 8} textAnchor="middle" fontSize="11" fill="#6b7280" letterSpacing="0.5">HEALTHY</text>
                            </svg>
                          </div>

                          {/* Legend */}
                          <div className="flex items-center justify-center gap-6 mt-1 px-2">
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                              <span className="text-[11px] text-gray-600">Urgent</span>
                              <span className="text-[10px] font-semibold text-gray-700">{urgent}</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                              <span className="text-[11px] text-gray-600">Attention</span>
                              <span className="text-[10px] font-semibold text-gray-700">{attention}</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              <span className="text-[11px] text-gray-600">Healthy</span>
                              <span className="text-[10px] font-semibold text-gray-700">{healthy}</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">{total} roles · {totalApplicants} applicants</p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      <Dialog open={showAddEventModal} onOpenChange={setShowAddEventModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-event-modal">
          <DialogHeader>
            <DialogTitle>New Calendar Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="evt-title" className="text-sm">Title</Label>
              <Input
                id="evt-title"
                placeholder="Interview with..."
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                data-testid="evt-title"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="evt-time" className="text-sm">Start time</Label>
                <Input
                  id="evt-time"
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                  data-testid="evt-time"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="evt-dur" className="text-sm">Duration</Label>
                <select
                  id="evt-dur"
                  value={newEvent.duration}
                  onChange={(e) => setNewEvent({ ...newEvent, duration: e.target.value })}
                  className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="evt-duration"
                >
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">60 min</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Will be created on <span className="font-medium text-gray-700">{selectedDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              {calendarConnected ? ' · Google Meet link will be generated automatically.' : ''}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddEventModal(false)} disabled={creatingEvent} data-testid="evt-cancel">Cancel</Button>
            <Button
              onClick={async () => {
                if (!newEvent.title.trim()) return;
                setCreatingEvent(true);
                try {
                  const [h, m] = newEvent.time.split(':').map(Number);
                  const start = new Date(selectedDate);
                  start.setHours(h, m, 0, 0);
                  const end = new Date(start.getTime() + parseInt(newEvent.duration, 10) * 60 * 1000);
                  await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`, {
                    userId: userProfile.id,
                    title: newEvent.title,
                    start: start.toISOString(),
                    end: end.toISOString(),
                  });
                  setShowAddEventModal(false);
                  fetchCalendarEvents(selectedDate);
                } catch (err) {
                  console.error('Failed to create event', err);
                  alert('Failed to create event. Please make sure Google Calendar is connected.');
                } finally {
                  setCreatingEvent(false);
                }
              }}
              disabled={creatingEvent || !newEvent.title.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="evt-save"
            >
              {creatingEvent ? 'Creating…' : 'Create event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All-notifications modal — scrollable list with "Close all" */}
      {showNotifModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowNotifModal(false)}
          data-testid="notifications-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="notifications-modal"
          >
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">All notifications</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {notifications.filter((n) => !dismissedNotifIds.has(n.id)).length} unread
                </p>
              </div>
              <button
                onClick={() => setShowNotifModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Close"
                data-testid="notifications-modal-close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  dismissAllVisible();
                }}
                disabled={notifications.filter((n) => !dismissedNotifIds.has(n.id)).length === 0}
                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                data-testid="notifications-modal-close-all"
              >
                Close all
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {notifications.filter((n) => !dismissedNotifIds.has(n.id)).length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400" data-testid="notifications-modal-empty">
                  You&apos;re all caught up.
                </div>
              ) : (
                notifications
                  .filter((n) => !dismissedNotifIds.has(n.id))
                  .map((notification) => (
                    <div
                      key={notification.id}
                      role={notification.applicationId ? 'button' : undefined}
                      tabIndex={notification.applicationId ? 0 : undefined}
                      onClick={() => {
                        if (notification.applicationId) {
                          setShowNotifModal(false);
                          navigate(`/candidates/${notification.applicationId}`);
                        }
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && notification.applicationId) {
                          e.preventDefault();
                          setShowNotifModal(false);
                          navigate(`/candidates/${notification.applicationId}`);
                        }
                      }}
                      className={`group relative border border-gray-200 rounded-lg p-3 bg-gray-50 hover:shadow-sm hover:border-gray-300 transition-all ${
                        notification.applicationId ? 'cursor-pointer' : ''
                      }`}
                      data-testid={`notifications-modal-item-${notification.id}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotif(notification.id);
                        }}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-gray-600 hover:bg-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        aria-label="Dismiss notification"
                        title="Dismiss"
                        data-testid={`notifications-modal-dismiss-${notification.id}`}
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                      <div className="flex gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0 pr-5">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <div className="font-medium text-sm text-blue-600 truncate">{notification.position}</div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-xs text-gray-700 truncate">{notification.applicantName}</span>
                                {notification.matchScore != null ? (
                                  <span
                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                      notification.matchScore >= 80
                                        ? 'bg-green-100 text-green-700'
                                        : notification.matchScore >= 60
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {notification.matchScore}% match
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-gray-400 whitespace-nowrap">Not scored yet</span>
                                )}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 text-right flex-shrink-0 ml-2">
                              <div>{notification.date}</div>
                              <div>{notification.time}</div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed mt-1.5 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}
    </RecruiterLayout>
  );
};

export default RecruiterDashboard;
